/**
 * Theme preference (auto / light / dark), dark variant and app icon. Persisted to localStorage so the first paint is
 * right (index.html reads the same keys) and mirrored into engine user settings so other devices follow.
 */
import { useEffect } from "react";
import { create } from "zustand";
import type { DarkThemeVariant, ThemePreference } from "@hub/shared";
import { getHost } from "@/engine/transport";
import { invokeEngine } from "@/engine/hooks";

interface ThemeState {
  theme: ThemePreference;
  darkTheme: DarkThemeVariant;
  appIcon: string | null;
  resolved: "light" | DarkThemeVariant;
  setTheme(theme: ThemePreference): void;
  setDarkTheme(variant: DarkThemeVariant): void;
  setAppIcon(id: string | null): void;
  hydrateFromSettings(s: { theme: ThemePreference; darkTheme: DarkThemeVariant; appIcon: string | null }): void;
}

function readPref(): ThemePreference {
  try {
    const v = localStorage.getItem("hub_theme");
    return v === "light" || v === "dark" || v === "auto" ? v : "auto";
  } catch {
    return "auto";
  }
}
function readDark(): DarkThemeVariant {
  try {
    const v = localStorage.getItem("hub_dark_theme");
    return v === "dark-black" ? "dark-black" : "dark-graphite";
  } catch {
    return "dark-graphite";
  }
}
function readIcon(): string | null {
  try {
    return localStorage.getItem("hub_app_icon");
  } catch {
    return null;
  }
}

function systemDark(): boolean {
  return typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia("(prefers-color-scheme: dark)").matches;
}

function resolve(theme: ThemePreference, dark: DarkThemeVariant): "light" | DarkThemeVariant {
  const isDark = theme === "dark" || (theme === "auto" && systemDark());
  return isDark ? dark : "light";
}

function apply(resolved: "light" | DarkThemeVariant) {
  if (typeof document === "undefined") return;
  document.documentElement.setAttribute("data-theme", resolved);
  const host = getHost();
  if (host?.shell?.setThemeBackground) {
    const bg = resolved === "light" ? "#fafafa" : resolved === "dark-black" ? "#000000" : "#1d1e20";
    host.shell.setThemeBackground(bg, resolved !== "light").catch(() => undefined);
  }
}

function persist(key: string, value: string | null) {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {
    // ignore
  }
}

function saveToEngine(patch: Record<string, unknown>) {
  invokeEngine("saveUserSettings", patch).catch(() => undefined);
}

export const useTheme = create<ThemeState>((set, get) => {
  const theme = readPref();
  const darkTheme = readDark();
  return {
    theme,
    darkTheme,
    appIcon: readIcon(),
    resolved: resolve(theme, darkTheme),
    setTheme(theme) {
      persist("hub_theme", theme);
      const resolved = resolve(theme, get().darkTheme);
      apply(resolved);
      set({ theme, resolved });
      saveToEngine({ theme });
    },
    setDarkTheme(variant) {
      persist("hub_dark_theme", variant);
      const resolved = resolve(get().theme, variant);
      apply(resolved);
      set({ darkTheme: variant, resolved });
      saveToEngine({ darkTheme: variant });
    },
    setAppIcon(id) {
      persist("hub_app_icon", id);
      set({ appIcon: id });
      getHost()?.shell?.setAppIcon(id).catch(() => undefined);
      saveToEngine({ appIcon: id });
    },
    hydrateFromSettings(s) {
      // Engine settings win only when localStorage has nothing yet (first run on a new device).
      let hasLocal = false;
      try {
        hasLocal = localStorage.getItem("hub_theme") !== null;
      } catch {
        hasLocal = false;
      }
      if (hasLocal) return;
      persist("hub_theme", s.theme);
      persist("hub_dark_theme", s.darkTheme);
      const resolved = resolve(s.theme, s.darkTheme);
      apply(resolved);
      set({ theme: s.theme, darkTheme: s.darkTheme, appIcon: s.appIcon, resolved });
    },
  };
});

/** Applies the theme on mount and follows the OS when the preference is auto. */
export function useApplyTheme() {
  const theme = useTheme((s) => s.theme);
  const darkTheme = useTheme((s) => s.darkTheme);
  useEffect(() => {
    apply(resolve(theme, darkTheme));
    if (theme !== "auto" || typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => {
      const resolved = resolve(theme, darkTheme);
      apply(resolved);
      useTheme.setState({ resolved });
    };
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, [theme, darkTheme]);
}

export const APP_ICONS: { id: string; name: string; color: string }[] = [
  { id: "default", name: "Blue", color: "#60a5fa" },
  { id: "graphite", name: "Graphite", color: "#2b2b2e" },
  { id: "violet", name: "Violet", color: "#a78bfa" },
  { id: "green", name: "Green", color: "#7cd48f" },
  { id: "light", name: "Light", color: "#fafafa" },
];
